import { AccessTime, Public, TrendingUp } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../services/api';

const CurrencyRatesPage = () => {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/currency/display-rates');
      
      const data = response.data;
      setRates(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to load currency rates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchRates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatRate = (rate) => {
    if (typeof rate !== 'number') return 'N/A';
    return rate.toFixed(4);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading currency rates...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Failed to load currency rates</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          <Public sx={{ mr: 2, verticalAlign: 'middle' }} />
          Live Currency Rates
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Real-time exchange rates from National Banks
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
          <AccessTime sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdated.toLocaleString('uk-UA')}
          </Typography>
        </Box>
      </Box>

      {/* Ukraine Rates */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
        🇺🇦 Ukrainian Hryvnia (UAH)
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Data from National Bank of Ukraine (NBU)
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">USD → UAH</Typography>
              </Box>
              <Typography variant="h3" component="div" color="primary">
                {formatRate(rates?.usd)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1 USD = {formatRate(rates?.usd)} UAH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="secondary" sx={{ mr: 2 }} />
                <Typography variant="h6">EUR → UAH</Typography>
              </Box>
              <Typography variant="h3" component="div" color="secondary">
                {formatRate(rates?.eur)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1 EUR = {formatRate(rates?.eur)} UAH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Kazakhstan Rates */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 2 }}>
        🇰🇿 Kazakhstani Tenge (KZT)
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Data from National Bank of Kazakhstan (NBK)
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="success" sx={{ mr: 2 }} />
                <Typography variant="h6">USD → KZT</Typography>
              </Box>
              <Typography variant="h3" component="div" color="success.main">
                {formatRate(rates?.kzt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1 USD = {formatRate(rates?.kzt)} KZT
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="warning" sx={{ mr: 2 }} />
                <Typography variant="h6">EUR → KZT</Typography>
              </Box>
              <Typography variant="h3" component="div" color="warning.main">
                {formatRate(rates?.kzt_eur)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                1 EUR = {formatRate(rates?.kzt_eur)} KZT
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer Info */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          About These Rates
        </Typography>
        <Typography variant="body2" paragraph>
          • Ukrainian rates are sourced directly from the National Bank of Ukraine (NBU) official API
        </Typography>
        <Typography variant="body2" paragraph>
          • Kazakhstan rates are sourced from the National Bank of Kazakhstan (NBK) official API
        </Typography>
        <Typography variant="body2" paragraph>
          • Rates are updated automatically every 24 hours at 09:00 server time
        </Typography>
        <Typography variant="body2" paragraph>
          • This page refreshes rates every 5 minutes for the most current display
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Chip 
            label="Live Data" 
            color="success" 
            variant="outlined" 
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip 
            label="Official Sources" 
            color="primary" 
            variant="outlined" 
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip 
            label="Auto-Updated" 
            color="secondary" 
            variant="outlined" 
            size="small"
          />
        </Box>
      </Box>
    </Container>
  );
};

export default CurrencyRatesPage;